import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

const PENDING_KEY = "paragon_pending_order";

export const rememberPendingOrder = (order, kind) =>
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ order_number: order.order_number, email: order.email, kind }));

export const startStripeCheckout = async (order, kind) => {
  rememberPendingOrder(order, kind);
  const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
  window.location.href = data.checkout_url;
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    const ref = params.get("ref");
    if (!ref) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${ref}`);
        if (data.payment_status === "paid") {
          const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "{}");
          sessionStorage.removeItem(PENDING_KEY);
          if (pending.kind === "wholesale") { navigate("/gym/orders", { replace: true }); return; }
          clear();
          if (pending.order_number && pending.email) {
            const { data: order } = await api.get(`/orders/track/${pending.order_number}`, { params: { email: pending.email } });
            navigate("/order-confirmation", { replace: true, state: { order } });
          } else {
            setState("paid");
          }
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch {
        /* keep polling */
      }
      tries.current += 1;
      if (tries.current < 20) timer = setTimeout(poll, 3000);
      else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  return (
    <div data-testid="payment-success-page" className="max-w-xl mx-auto px-6 py-24 text-center">
      {state === "checking" && <p data-testid="payment-checking" className="font-mono text-yellow-400 uppercase tracking-widest">Confirming your payment…</p>}
      {state === "paid" && <p data-testid="payment-paid" className="font-heading text-3xl text-white uppercase">Payment received. Thank you!</p>}
      {state === "timeout" && <p data-testid="payment-timeout" className="text-zinc-300">Payment is still processing. You'll get a confirmation email as soon as it clears.</p>}
      {state === "error" && <p data-testid="payment-error" className="text-red-400">We couldn't confirm this payment. Your card was not charged. Please try again.</p>}
      {state !== "checking" && <Link to="/shop" className="inline-block mt-6 text-yellow-400 font-mono text-xs uppercase">Back to shop</Link>}
    </div>
  );
}

export function PaymentCancel() {
  const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "{}");
  const back = pending.kind === "wholesale" ? "/gym" : "/cart";
  return (
    <div data-testid="payment-cancel-page" className="max-w-xl mx-auto px-6 py-24 text-center">
      <h1 className="font-heading font-black uppercase text-3xl text-white">Payment cancelled</h1>
      <p className="text-zinc-400 mt-3">No charge was made. Your items are still saved, so you can check out whenever you're ready.</p>
      <Link to={back} data-testid="payment-cancel-back" className="inline-block mt-6 btn-primary px-6 py-3 rounded-sm text-sm">
        {pending.kind === "wholesale" ? "Back to portal" : "Back to cart"}
      </Link>
    </div>
  );
}
